/**
 * GroupMembersWidget Component
 *
 * Виджет для управления членами группы компаний.
 * Отображается только для организаций с is_company_group=true.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Group,
  Text,
  Stack,
  Button,
  Box,
  Badge,
  ActionIcon,
  Menu,
  Loader,
  Center,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconBuilding,
  IconPlus,
  IconDots,
  IconExternalLink,
  IconTrash,
  IconMapPin,
  IconStar,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import BaseWidget from './BaseWidget';
import AddMemberModal from './AddMemberModal';
import tokens from '../../../theme/designTokens';

// Конфигурация badge-ей для этапов воронки
const STAGE_BADGES = {
  'new': { color: '#0176D3', label: 'Новый' },
  'message_sent': { color: '#7DC681', label: 'Сообщ.' },
  'call': { color: '#5EBA62', label: 'Звонок' },
  'meeting': { color: '#48A84C', label: 'Встреча' },
  'master_class': { color: '#3D9141', label: 'МК' },
  'contract_signed': { color: '#2E7D32', label: 'Договор' },
  'classes_active': { color: '#1B5E20', label: 'Занятия' },
  'classes_paused': { color: '#FFA000', label: 'Пауза' },
  'contract_terminated': { color: '#C62828', label: 'Растор.' },
};

/**
 * API функции для членов группы
 */
const groupMembersApi = {
  async getMembers(groupId) {
    const response = await fetch(`/api/company-groups/${groupId}/members`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить компании группы');
    }
    return response.json();
  },

  async removeMember(groupId, organizationId) {
    const response = await fetch(`/api/company-groups/${groupId}/members/${organizationId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось удалить организацию из группы');
    }
  },

  async addMember(groupId, organizationId) {
    const response = await fetch(`/api/company-groups/${groupId}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ organization_id: organizationId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось добавить организацию в группу');
    }
    return response.json();
  },
};

/**
 * Компонент отображения члена группы
 */
const MemberItem = ({ member, onRemove, onView }) => {
  const stageBadge = STAGE_BADGES[member.cooperation_stage] || {
    color: '#999999',
    label: member.cooperation_stage || '—'
  };

  return (
    <Group
      p="sm"
      style={{
        borderRadius: '12px',
        backgroundColor: tokens.colors.neutral[1],
        transition: 'background-color 150ms ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = tokens.colors.neutral[2]}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = tokens.colors.neutral[1]}
      onClick={() => onView(member)}
    >
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500} truncate>
            {member.name}
          </Text>
          <Badge
            variant="filled"
            size="xs"
            radius="xl"
            styles={{
              root: {
                backgroundColor: stageBadge.color,
                color: 'white',
              }
            }}
          >
            {stageBadge.label}
          </Badge>
        </Group>

        <Text size="xs" c="dimmed" truncate>
          {member.category}
        </Text>

        {member.address && (
          <Group gap={4} mt={4}>
            <IconMapPin size={12} color={tokens.colors.neutral[5]} />
            <Text size="xs" c="dimmed" truncate>
              {member.address}
            </Text>
          </Group>
        )}

        {/* Рейтинг */}
        {member.rating && (
          <Group gap={4} mt={4}>
            <IconStar size={12} color="#FFA000" fill="#FFA000" />
            <Text size="xs" c="dimmed">
              {member.rating} ({member.review_count || 0})
            </Text>
          </Group>
        )}
      </Box>

      {/* Меню действий */}
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray" onClick={(e) => e.stopPropagation()}>
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
          <Menu.Item
            leftSection={<IconExternalLink size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onView(member);
            }}
          >
            Открыть
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconTrash size={14} />}
            color="red"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(member);
            }}
          >
            Удалить из группы
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};

/**
 * Основной компонент виджета
 */
const GroupMembersWidget = ({ organization, onActivityChange }) => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Модальное окно добавления
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Загрузка членов группы
  const loadMembers = useCallback(async () => {
    if (!organization?.id || !organization?.is_company_group) return;

    try {
      setLoading(true);
      const data = await groupMembersApi.getMembers(organization.id);
      setMembers(data || []);
    } catch (err) {
      console.error('Failed to load group members:', err);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, organization?.is_company_group]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // Если не группа компаний - не показываем виджет
  if (!organization?.is_company_group) {
    return null;
  }

  // Открыть организацию
  const handleViewMember = (member) => {
    navigate(`/organizations/${member.id}`);
  };

  // Удалить из группы
  const handleRemoveMember = (member) => {
    modals.openConfirmModal({
      title: 'Удаление из группы',
      centered: true,
      children: (
        <Text size="sm">
          Вы уверены, что хотите удалить «{member.name}» из группы компаний?
        </Text>
      ),
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await groupMembersApi.removeMember(organization.id, member.id);
          notifications.show({
            title: 'Удалено',
            message: `«${member.name}» удалена из группы`,
            color: 'green',
          });
          await loadMembers();
          // Обновляем историю активности
          if (onActivityChange) {
            onActivityChange();
          }
        } catch (err) {
          console.error('Failed to remove member:', err);
          notifications.show({
            title: 'Ошибка',
            message: err.message,
            color: 'red',
          });
        }
      },
    });
  };

  // Добавить в группу
  const handleAddMember = async (organizationId) => {
    try {
      await groupMembersApi.addMember(organization.id, organizationId);
      setAddModalOpen(false);
      notifications.show({
        title: 'Добавлено',
        message: 'Организация добавлена в группу',
        color: 'green',
      });
      await loadMembers();
      // Обновляем историю активности
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      throw err;
    }
  };

  return (
    <>
      <BaseWidget
        title="Компании группы"
        icon={IconBuilding}
        count={members.length}
        collapsible={true}
        defaultExpanded={true}
        actions={
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              setAddModalOpen(true);
            }}
          >
            Добавить
          </Button>
        }
      >
        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : members.length > 0 ? (
          <Stack gap="xs">
            {members.map((member) => (
              <MemberItem
                key={member.id}
                member={member}
                onRemove={handleRemoveMember}
                onView={handleViewMember}
              />
            ))}
          </Stack>
        ) : (
          <Box py="lg" style={{ textAlign: 'center' }}>
            <IconBuilding
              size={40}
              style={{ color: tokens.colors.neutral[4], marginBottom: '8px' }}
            />
            <Text size="sm" c="dimmed">
              В группе нет компаний
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              Добавьте организации в группу компаний
            </Text>
            <Button
              variant="light"
              size="xs"
              mt="md"
              leftSection={<IconPlus size={14} />}
              onClick={() => setAddModalOpen(true)}
            >
              Добавить организацию
            </Button>
          </Box>
        )}
      </BaseWidget>

      {/* Модальное окно добавления */}
      <AddMemberModal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAddMember}
        groupId={organization?.id}
      />
    </>
  );
};

export default GroupMembersWidget;
